import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateSommelierDto, UpdateSommelierDto, SommelierDto, CreateSommelierWithUserDto, UpdateSommelierWithUserDto, ApproveSommelierDto } from '../../proxy/sommeliers/models';
import { SommelierService } from '@proxy/sommeliers';
import { PickUpService } from '@proxy/pick-ups';
import { PickUpDto } from '../../proxy/pick-ups/models';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, firstValueFrom, map, Observable, take } from 'rxjs';
import { StorageService } from 'src/app/shared/services/storage.service';
import { ARGENTINA_PROVINCES } from '../../shared/constants/argentina-provinces';
import { SelectOption } from '../../shared/components/metro-form-select/metro-form-select.component';
import { LocalityService, LocalityCatalog } from '../../proxy/localities';

export const COUNTRY_DIAL_CODES: { code: string; label: string }[] = [
  { code: '+549', label: '+549 (Argentina)' },
  { code: '+55',  label: '+55 (Brasil)' },
  { code: '+56',  label: '+56 (Chile)' },
  { code: '+595', label: '+595 (Paraguay)' },
  { code: '+598', label: '+598 (Uruguay)' },
  { code: '+591', label: '+591 (Bolivia)' },
  { code: '+51',  label: '+51 (Perú)' },
  { code: '+57',  label: '+57 (Colombia)' },
  { code: '+58',  label: '+58 (Venezuela)' },
  { code: '+593', label: '+593 (Ecuador)' },
  { code: '+1',   label: '+1 (EE.UU. / Canadá)' },
  { code: '+52',  label: '+52 (México)' },
  { code: '+34',  label: '+34 (España)' },
  { code: '+39',  label: '+39 (Italia)' },
  { code: '+44',  label: '+44 (Reino Unido)' },
  { code: '+49',  label: '+49 (Alemania)' },
  { code: '+33',  label: '+33 (Francia)' },
];

@Component({
  selector: 'app-sommelier-form',
  templateUrl: './sommelier-form.component.html',
  standalone: false
})
export class SommelierFormComponent implements OnInit {
  @Input() sommelier?: SommelierDto | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<SommelierDto>();
  selectedFile: File | null = null;
  imagePreviewUrl: string | null = null;

  form!: FormGroup;
  isEditMode = false;
  isEditingSlug = false;
  currentSlug = '';

  countryDialCodes = COUNTRY_DIAL_CODES;
  // "Otra" — para los sommeliers con datos históricos ambiguos ("Cuyo"/"Patagonia"/etc.,
  // ver #334); el admin o el propio sommelier la corrige después.
  provinceOptions: SelectOption[] = [
    ...ARGENTINA_PROVINCES.map(p => ({ value: p, label: p })),
    { value: 'Otra', label: 'Otra' },
  ];

  // winepassport#343 / #354 — combo dependiente provincia -> localidad.
  private localityCatalog: LocalityCatalog = {};
  localityOptions: SelectOption[] = [];

  users$: Observable<{ value: string, label: string }[]>;

  // Variable para almacenar los pickups disponibles
  pickups$: Observable<Array<{ value: number, label: string }>> = new Observable();

  // Variable para almacenar los pickups seleccionados inicialmente
  selectedPickups: number[] = [];


  constructor(
    private fb: FormBuilder,
    private sommelierService: SommelierService,
    private pickupService: PickUpService,
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService,
    private localityService: LocalityService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    // winepassport#343 / #354 — catálogo provincia -> localidad para el combo dependiente.
    this.localityService.getAll().subscribe(catalog => {
      this.localityCatalog = catalog || {};
      this.refreshLocalityOptions();
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.sommelierService.get(Number(id)).subscribe(sommelier => {
        this.sommelier = sommelier;
        this.currentSlug = sommelier.slug || '';
        // Guardar los pickups seleccionados inicialmente
        this.selectedPickups = sommelier.pickUps.map(pickup => pickup.id);
        this.patchForm(sommelier);
        this.form.get('email')?.disable();
        this.refreshLocalityOptions();
      }
      );
    }

    this.users$ = this.sommelierService.getUsers().pipe(
      distinctUntilChanged(),
      map(val => val.map(item => {
        return { label: item.name, value: item.id.toString() };
      }))
    );

    // Cargar los pickups activos
    this.pickups$ = this.pickupService.getPickUps().pipe(
      map(pickups => pickups.map(pickup => ({
        value: pickup.id,
        label: `${pickup.name} - ${pickup.address}`
      })))
    );

    this.users$.subscribe(() => this.tryPatchForm());
  }

  startEditingSlug() {
    this.isEditingSlug = true;
  }

  cancelEditingSlug() {
    this.isEditingSlug = false;
    // Restore the original slug value if user cancels
    if (this.sommelier) {
      this.currentSlug = this.sommelier.slug || '';
    }
  }

  saveSlug() {
    if (!this.sommelier || !this.currentSlug.trim()) {
      return;
    }

    const updateDto = { slug: this.currentSlug.trim() };
    this.sommelierService.updateSommelierSlug(this.sommelier.id, updateDto).subscribe({
      next: (updatedSommelier) => {
        this.sommelier = updatedSommelier;
        this.isEditingSlug = false;
        // Update the form values if needed
        this.patchForm(updatedSommelier);
      },
      error: (error) => {
        console.error('Error updating sommelier slug:', error);
        // Optionally show an error message to the user
      }
    });
  }

  onImageSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  private tryPatchForm() {
    if (!this.form || !this.sommelier) return;

    const { countryDialCode, localPhoneNumber } = this.splitPhone(this.sommelier.phoneNumber);
    this.users$.pipe(take(1)).subscribe(options => {
      const userExists = options.some(o => o.value === this.sommelier!.userId.toString());
      this.form.patchValue({
        ...this.sommelier,
        userId: userExists ? this.sommelier!.userId.toString() : null,
        pickUpIds: this.sommelier!.pickUps.map(p => p.id),
        countryDialCode,
        localPhoneNumber,
      });
    });
  }


  private buildForm(): void {
    this.form = this.fb.group({
      // --- Datos del usuario ---
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      surname: ['', [Validators.required]],
      countryDialCode: ['+549', [Validators.required]],
      localPhoneNumber: ['', [Validators.required]],

      // --- Datos del sommelier ---
      fileAttachmentId: [''],
      bio: [''],
      country: [''],
      region: [''],
      city: [''],
      address: [''],
      specialities: [''],
      certification: [''],
      paymentAccountDetails: [''],
      postalCode: [''],
      academicInstitution: [''],
      instagram: [''],
      commissionRate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      status: [''],
      isActive: [true],
      pickUpIds: [[], Validators.required]
    }, { validators: this.passwordsMatchValidator });
  }

  // Validador personalizado para confirmar password
  private passwordsMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  private splitPhone(e164?: string | null): { countryDialCode: string; localPhoneNumber: string } {
    if (!e164) return { countryDialCode: '+549', localPhoneNumber: '' };
    const sorted = [...COUNTRY_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
    for (const c of sorted) {
      if (e164.startsWith(c.code)) {
        return { countryDialCode: c.code, localPhoneNumber: e164.slice(c.code.length) };
      }
    }
    return { countryDialCode: '+549', localPhoneNumber: e164.replace(/^\+/, '') };
  }

  private patchForm(sommelier: SommelierDto) {
    this.isEditMode = true;
    const { countryDialCode, localPhoneNumber } = this.splitPhone(sommelier.phoneNumber);
    this.form.patchValue({
      ...sommelier,
      countryDialCode,
      localPhoneNumber,
      pickUpIds: sommelier.pickUps.map(p => p.id)
    });

    if (this.sommelier!.fileAttachmentId) {
      this.storageService.getImage(this.sommelier.fileAttachmentId, 50, 100).subscribe(blob => {
        this.imagePreviewUrl = URL.createObjectURL(blob);
      });
    }
  }

  // ── Combo dependiente provincia → localidad (winepassport#343 / #354) ──────
  /** true cuando la localidad se pide como texto libre: provincia "Otra", vacía,
   *  o sin localidades en el catálogo. */
  get cityAsFreeText(): boolean {
    const region = (this.form?.get('region')?.value || '').trim();
    if (!region || region === 'Otra') return true;
    return !(this.localityCatalog[region]?.length);
  }

  private refreshLocalityOptions(): void {
    const region = (this.form?.get('region')?.value || '').trim();
    const list = this.localityCatalog[region] || [];
    this.localityOptions = list.map(l => ({ value: l, label: l }));
  }

  onRegionChange(value: string): void {
    this.form.get('region')?.setValue(value);
    // Si la localidad cargada no pertenece a la nueva provincia, se limpia.
    const city = (this.form.get('city')?.value || '').trim();
    const list = this.localityCatalog[value] || [];
    if (city && list.length && !list.some(l => l.toLowerCase() === city.toLowerCase())) {
      this.form.get('city')?.setValue('');
    }
    this.refreshLocalityOptions();
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired ';
      if (control.errors?.['min']) return '::CommissionRateMustBeBetween0And100 ';
      if (control.errors?.['max']) return '::CommissionRateMustBeBetween0And100 ';
      // winepassport#380 — caso puntual que disparó el ticket: cargar el código de país
      // de nuevo dentro del campo del número (ej. "+549" + "54911...") supera el largo
      // máximo del teléfono en el servidor y volvía como un error crudo en inglés.
      if (control.errors?.['countryCodeRepeated']) {
        return 'No incluyas el código de país acá — ya se agrega con el selector de arriba.';
      }
    }
    return undefined;
  }

  // winepassport#380 — el código de país (ej. "549") no debería repetirse al principio del
  // número local; si pasa, el teléfono final supera el largo máximo de la columna en el
  // servidor y el admin veía una excepción cruda de EF/Postgres en vez de un aviso claro.
  private phoneHasDuplicatedCountryCode(): boolean {
    const dialDigits = (this.form.get('countryDialCode')?.value || '').replace(/\D+/g, '');
    const localDigits = (this.form.get('localPhoneNumber')?.value || '').replace(/\D+/g, '');
    return !!dialDigits && !!localDigits && localDigits.startsWith(dialDigits);
  }

  private blockSubmitIfPhoneHasDuplicatedCountryCode(): boolean {
    if (!this.phoneHasDuplicatedCountryCode()) return false;
    this.form.get('localPhoneNumber')?.setErrors({ countryCodeRepeated: true });
    this.form.get('localPhoneNumber')?.markAsTouched();
    return true;
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.blockSubmitIfPhoneHasDuplicatedCountryCode()) {
      return;
    }

    let newFileAttachmentId: number | null = null;
    if (this.selectedFile) {
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      newFileAttachmentId = await firstValueFrom(
        this.sommelierService.uploadFileByFile(formData)
      );
    }

    // Obtener el valor actual del formulario, incluyendo los pickUpIds seleccionados
    const formValue = this.form.getRawValue();

    const localDigits = (formValue.localPhoneNumber || '').replace(/\D+/g, '');
    const phoneNumber = localDigits ? `${formValue.countryDialCode}${localDigits}` : undefined;

    const dto = { ...formValue, phoneNumber } as CreateSommelierWithUserDto | UpdateSommelierDto;

    // Asegurarse de que los pickUpIds se incluyan correctamente
    dto.pickUpIds = formValue.pickUpIds || [];

    dto.fileAttachmentId = newFileAttachmentId ?? this.sommelier?.fileAttachmentId;
    const oldFileAttachmentId = this.sommelier?.fileAttachmentId;

    if (this.isEditMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.sommelierService.updateSommelier(Number(id), dto as UpdateSommelierDto)
          .subscribe(result => {
            if (newFileAttachmentId && oldFileAttachmentId) {
              this.sommelierService.deleteFileByFileAttachmentId(oldFileAttachmentId).subscribe();
            }
            this.handleSuccess(result);
          });
      }
    } else {
      this.sommelierService.createWithUser(dto as CreateSommelierWithUserDto)
        .subscribe(result => this.handleSuccess(result));
    }
  }

  onCancel() {
    this.router.navigate(['/sommeliers']);
  }

  activateSommelier() {
    if (this.blockSubmitIfPhoneHasDuplicatedCountryCode()) {
      return;
    }

    const formValue = this.form.getRawValue();
    const localDigits = (formValue.localPhoneNumber || '').replace(/\D+/g, '');
    const phoneNumber = localDigits ? `${formValue.countryDialCode}${localDigits}` : undefined;

    const id = this.route.snapshot.paramMap.get('id');
    const dto = {
      ...formValue,
      phoneNumber,
      sommelierId: id
    } as ApproveSommelierDto;

    dto.pickUpIds = formValue.pickUpIds || [];

    this.sommelierService.approveSommelier(dto).subscribe(res => {
      this.handleSuccess(res)
    })
  }

  private handleSuccess(result: SommelierDto) {
    this.saved.emit(result);
    this.router.navigate(['/sommeliers']);
  }
}
